import { toCurrency, toPercentage } from '@simplywallst/formatting';
import { requestWithAuth } from '@utilities/graphQLClient';
import { gql } from 'graphql-request';
import type { QueryFunctionContext } from 'react-query';
import { isNull } from 'lodash';
import watchlistSortingToQueryInput from './watchlistSortingToQueryInput';

import type {
  Watchlist,
  WatchlistItem,
  WatchlistCompany,
  WatchlistSorting,
} from './types';

type WatchlistCompanyMap = Record<
  WatchlistCompany['companyId'],
  WatchlistCompany
>;

export interface Response {
  id: string;
  name: string;
  companyMap: WatchlistCompanyMap;
  keys: string[];
  itemsLimit: number;
}

export interface QueryResponse {
  errors?: Error[];
  Watchlist: {
    id: Watchlist['id'];
    name: Watchlist['name'];
    items: Watchlist['items'];
  };
}

export const watchlistItemFragment = gql`
  fairValue
  companyId
  autoAdded
  createdAt
  company {
    name
    country {
      id
    }
    tickerSymbol
    uniqueSymbol
    canonicalURL
    exchangeCountryISO
    primaryIndustry {
      id
      name
    }
    secondaryIndustry {
      name
    }
    score {
      value
      future
      past
      health
      dividend
    }
    data {
      currencyInfo
    }
    analysisValue {
      lastSharePrice
      npvPerShare
      marketCapUSD
      intrinsicDiscount
      return7d
      return30d
      return90d
      return1yAbs
      priceTarget
      priceTargetAnalystCount
    }
    analysisHealth {
      netIncome
    }
  }
`;

const formatterPercentage = (value: number | null) =>
  value !== null ? toPercentage(value, 1) : 'n/a';

export const formatterCurrency = (value: number | null, currencyISO: string) =>
  value !== null ? toCurrency(value, 2, currencyISO) : 'n/a';

const getIsProfitable = (netIncome: number | null): boolean => {
  if (netIncome === null || netIncome <= 0) return false;
  return true;
};

function getAnalystsCountFormatted(count: number | null): string {
  if (count === null) return '0 Analysts';
  return `${count} ${count === 1 ? 'Analyst' : 'Analysts'}`;
}

export const getDefault = (): WatchlistCompany => ({
  companyId: '',
  exchangeCountryISO: null,
  currencyISO: '',
  userFairValue: null,
  userFairValueFormatted: '',
  name: '',
  createdAt: 0,
  canonicalUrl: '',
  tickerSymbol: '',
  uniqueSymbol: '',
  marketCapUSD: null,
  sharePrice: null,
  sharePriceFormatted: '',
  priceTarget: null,
  priceTargetFormatted: '',
  priceTargetAnalystCount: null,
  priceTargetAnalystCountFormatted: '',
  intrinsicDiscount: null,
  intrinsicDiscountFormatted: '',
  npvPerShare: null,
  npvPerShareFormatted: '',
  return7D: null,
  return7DFormatted: '',
  return30D: null,
  return30DFormatted: '',
  return90D: null,
  return90DFormatted: '',
  return1YrAbs: null,
  return1YrAbsFormatted: '',
  snowflakePoints: [0, 0, 0, 0, 0],
  countryId: null,
  industryId: null,
  industryName: '',
  isProfitable: false,
  autoAdded: null,
});

export const companyMapper = (data: WatchlistItem[]) => {
  return data.reduce<WatchlistCompanyMap>((a, b) => {
    try {
      const id = b.companyId;
      if (typeof a[id] === 'undefined') {
        a[id] = getDefault();
      }
      a[id].companyId = id;
      a[id].countryId = b.company.country.id;
      a[id].industryId = b.company.primaryIndustry.id;
      a[id].industryName = b.company.primaryIndustry.name;
      a[id].exchangeCountryISO = b.company.exchangeCountryISO;
      a[id].currencyISO = b.company.data.currencyInfo.trading_item_currency_iso;
      a[id].snowflakePoints = [
        b.company.score.value,
        b.company.score.future,
        b.company.score.past,
        b.company.score.health,
        b.company.score.dividend,
      ].map((score) => score + 1);
      a[id].name = b.company.name;
      a[id].createdAt = b.createdAt;
      a[id].canonicalUrl = b.company.canonicalURL;
      a[id].tickerSymbol = b.company.tickerSymbol;
      a[id].uniqueSymbol = b.company.uniqueSymbol;
      a[id].isProfitable = getIsProfitable(b.company.analysisHealth.netIncome);
      a[id].autoAdded = b.autoAdded;
      a[id].marketCapUSD = b.company.analysisValue.marketCapUSD;
      a[id].priceTargetAnalystCount =
        b.company.analysisValue.priceTargetAnalystCount;
      a[id].priceTargetAnalystCountFormatted = getAnalystsCountFormatted(
        b.company.analysisValue.priceTargetAnalystCount
      );
      a[id].priceTarget = b.company.analysisValue.priceTarget;
      a[id].priceTargetFormatted = formatterCurrency(
        b.company.analysisValue.priceTarget,
        b.company.data.currencyInfo.trading_item_currency_iso
      );
      a[id].npvPerShare = b.company.analysisValue.npvPerShare;
      a[id].npvPerShareFormatted = formatterCurrency(
        b.company.analysisValue.npvPerShare,
        b.company.data.currencyInfo.trading_item_currency_iso
      );
      a[id].return7D = b.company.analysisValue.return7d;
      a[id].return7DFormatted = formatterPercentage(
        b.company.analysisValue.return7d
      );
      a[id].return30D = b.company.analysisValue.return30d;
      a[id].return30DFormatted = formatterPercentage(
        b.company.analysisValue.return30d
      );
      a[id].return90D = b.company.analysisValue.return90d;
      a[id].return90DFormatted = formatterPercentage(
        b.company.analysisValue.return90d
      );
      a[id].return1YrAbs = b.company.analysisValue.return1yAbs;
      a[id].return1YrAbsFormatted = formatterPercentage(
        b.company.analysisValue.return1yAbs
      );
      a[id].sharePrice = b.company.analysisValue.lastSharePrice;
      a[id].sharePriceFormatted = formatterCurrency(
        b.company.analysisValue.lastSharePrice,
        b.company.data.currencyInfo.trading_item_currency_iso
      );
      a[id].userFairValue = b.fairValue;
      a[id].userFairValueFormatted = formatterCurrency(
        b.fairValue,
        b.company.data.currencyInfo.trading_item_currency_iso
      );
      a[id].intrinsicDiscount = b.company.analysisValue.intrinsicDiscount;
      a[id].intrinsicDiscountFormatted = formatterPercentage(
        isNull(b.company.analysisValue.intrinsicDiscount)
          ? null
          : Math.abs(b.company.analysisValue.intrinsicDiscount)
      );
    } catch (error) {
      console.log(error);
    }
    return a;
  }, {});
};

export const getDefaultResponse = (): Response => ({
  id: '',
  name: '',
  companyMap: {},
  keys: [],
  itemsLimit: 50,
});

export const mapWatchlistResponseToCache = (
  watchlist: Partial<Watchlist>
): Response => ({
  id: watchlist.id ?? '',
  name: watchlist.name ?? '',
  companyMap: companyMapper(watchlist.items || []),
  keys: watchlist.items?.map((n) => n.companyId) || [],
  itemsLimit: watchlist.itemsLimit ?? 50,
});

export const queryFn = async ({
  queryKey: [_key, id, sorting],
}: QueryFunctionContext<
  [string, string | null, WatchlistSorting]
>): Promise<Response> => {
  try {
    const response = await requestWithAuth<QueryResponse>(
      gql`
        query getWatchlist($sorting: WatchlistItemSortingInput!, $id: String) {
          Watchlist(id: $id) {
            id
            name
            items(sort: $sorting) {
              ${watchlistItemFragment}
            }
            itemsLimit
          }
        }
      `,
      {
        sorting: watchlistSortingToQueryInput(sorting),
        id: id,
      }
    );
    const { Watchlist, errors } = response;
    if (errors && errors.length > 0) {
      console.log(errors[0].message);
      throw new Error(errors[0].message);
    }
    if (Watchlist) {
      return mapWatchlistResponseToCache(Watchlist);
    }
    return getDefaultResponse();
  } catch (error) {
    throw error;
  }
};
